import styles from "./styles.module.scss";
import { getIcon, getSizeAbbreviation } from "./helper";
import classNames from "classnames";
import { EventTypeInfo, ScheduleStatus } from "./constants";
import { EventType, ScheduleCard } from "./types";
import dayjs from "dayjs";
import { Stack } from "@altimateai/lego";

const getIconTooltipText = (type: EventType) => {
  return EventTypeInfo[type].label;
};

export const WarehouseSizeChange = ({ event }: { event: ScheduleCard }) => {
  const getWarehouseSizeClass = (event: ScheduleCard) => {
    switch (event.type) {
      case EventTypeInfo.recommended.key:
        if (event.status === ScheduleStatus.Approved) {
          return "recommended";
        }

        if (event.status === ScheduleStatus.Rejected) {
          return "deleted";
        }

        return "default";

      case EventTypeInfo.custom.key:
        if (event.status === ScheduleStatus.Approved) {
          return "custom-applied";
        }

        return "custom";
      default:
        return event.type;
    }
  };

  const isExpired = dayjs(event.end).isBefore(dayjs());
  const isOngoing = dayjs(event.start).isBefore(dayjs()) && dayjs(event.end).isAfter(dayjs());

  const getStatusText = () => {
    if (event.type === "default") return "Default";
    if (event.type === "block") return "Blocked";
    if (event.type === "auto_resize") {
      if (event.status === ScheduleStatus.Approved) {
        return isExpired ? "Decision" : `${isOngoing ? "Ongoing" : "Upcoming"} Decision`;
      }
      return getIconTooltipText(event.type);
    }
    if (event.type === "backoff") {
      return event.status === ScheduleStatus.Approved
        ? "Backoff Applied"
        : getIconTooltipText(event.type);
    }
    return event.status === ScheduleStatus.Approved ? "Applied" : getIconTooltipText(event.type);
  };

  const statusText = getStatusText();

  return (
    <div
      className={classNames(styles.warehouseSizeChange, getWarehouseSizeClass(event), {
        [styles.autoResize]: event.type === "auto_resize",
        [styles.backoff]: event.type === "backoff",
        [styles.defaultSchedule]: event.type === "default",
        [styles.expired]: isExpired,
        [styles.expiredApproved]:
          isExpired && event.type !== "block" && event.status === ScheduleStatus.Approved,
      })}
      aria-label={`Event status ${statusText}${event.sizeTo && event.type !== "block" && event.type !== "backoff" ? ` size ${getSizeAbbreviation(event.sizeTo)}` : ""}`}
    >
      <Stack align="center">
        {event.type === "block" && <div>Blocked</div>}
        {event.type !== "block" &&
          (event.type === "auto_resize" || event.type === "backoff" ? (
            isExpired ? (
              getIcon({ type: event.type, change: event.change })
            ) : (
              <div></div>
            )
          ) : (
            getIcon({ type: event.type, change: event.change })
          ))}

        {event.type !== "block" && event.type !== "auto_resize" && event.type !== "backoff" && (
          <span>
            {event.type === "default"
              ? "Default"
              : event.status === ScheduleStatus.Approved
                ? "Applied"
                : getIconTooltipText(event.type)}
          </span>
        )}

        {event.type === "auto_resize" && (
          <span>
            {event.status === ScheduleStatus.Approved
              ? isExpired
                ? "Decision"
                : `${isOngoing ? "Ongoing" : "Upcoming"} Decision`
              : getIconTooltipText(event.type)}
          </span>
        )}

        {event.type === "backoff" && (
          <span>
            {event.status === ScheduleStatus.Approved
              ? "Backoff Applied"
              : getIconTooltipText(event.type)}
          </span>
        )}
      </Stack>
      {event.sizeTo && event.type !== "block" && event.type !== "backoff" ? (
        <div
          className={classNames(styles.sizeRecommendation, {
            [styles.recommended]: event.type === "recommended",
            [styles.custom]: event.type === "custom",
            [styles.autoResize]: event.type === "auto_resize",
            [styles.defaultSchedule]: event.type === "default",
            [styles.expired]: isExpired,
          })}
        >
          {getSizeAbbreviation(event.sizeTo || "")}
        </div>
      ) : (
        // This div is to avoid unneccessary displacement because of al-justify-between
        <div></div>
      )}
    </div>
  );
};

export const WarehouseSizeChangeInsight = ({ from, to }: { from: string; to: string }) => {
  return (
    <div className={styles.warehouseSizeChangeInsight}>
      <span className={styles.from}>{from}</span> -{">"} <span className={styles.to}>{to}</span>
    </div>
  );
};
